import { z } from 'zod';

export const TenantFiscalDefaultsMetadataSchema = z.looseObject({
  notes: z.string().max(500).optional(),
});

export const UpsertTenantFiscalDefaultsBodySchema = z.looseObject({
  defaultInboundCfopCode: z.string().length(4).nullable().optional(),
  defaultOutboundCfopCode: z.string().length(4).nullable().optional(),
  metadata: TenantFiscalDefaultsMetadataSchema.nullable().optional(),
});

export const TenantFiscalDefaultsResponseSchema = z.looseObject({
  id: z.uuid(),
  defaultInboundCfopCode: z.string().nullable(),
  defaultOutboundCfopCode: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  updatedAt: z.iso.datetime(),
});

export type UpsertTenantFiscalDefaultsBody = z.infer<typeof UpsertTenantFiscalDefaultsBodySchema>;
export type TenantFiscalDefaultsResponse = z.infer<typeof TenantFiscalDefaultsResponseSchema>;

export const UpsertTenantProductTaxProfileBodySchema = z.looseObject({
  originCode: z.string().length(1),
  defaultCfopInternalCode: z.string().length(4).nullable().optional(),
  defaultCfopInterstateCode: z.string().length(4).nullable().optional(),
  ncmCode: z.string().max(10).nullable().optional(),
  cestCode: z.string().max(10).nullable().optional(),
  taxBenefitCodeId: z.uuid().nullable().optional(),
  pisCstCode: z.string().max(2).nullable().optional(),
  cofinsCstCode: z.string().max(2).nullable().optional(),
  ipiCstCode: z.string().max(2).nullable().optional(),
  isImported: z.boolean().optional(),
  isFuel: z.boolean().optional(),
  validFrom: z.iso.date().optional(),
  validUntil: z.iso.date().nullable().optional(),
});

export const UpsertTenantProductTaxProfileResponseSchema = z.looseObject({
  profileId: z.uuid(),
  tenantProductId: z.uuid(),
  validFrom: z.iso.date(),
  validUntil: z.iso.date().nullable(),
});

export const ListProductTaxProfilesResponseSchema = z.looseObject({
  items: z.array(
    z.looseObject({
      id: z.uuid(),
      originCode: z.string(),
      ncmCode: z.string().nullable(),
      cestCode: z.string().nullable(),
      defaultCfopInternalCode: z.string().nullable(),
      defaultCfopInterstateCode: z.string().nullable(),
      pisCstCode: z.string().nullable(),
      cofinsCstCode: z.string().nullable(),
      ipiCstCode: z.string().nullable(),
      isImported: z.boolean(),
      isFuel: z.boolean(),
      validFrom: z.iso.date(),
      validUntil: z.iso.date().nullable(),
    }),
  ),
});

export const UpsertTenantServiceTaxProfileBodySchema = z.looseObject({
  taxCodeNational: z.string().max(20).nullable().optional(),
  municipalServiceCode: z.string().max(20).nullable().optional(),
  lc116Code: z.string().max(10).nullable().optional(),
  cnaeCode: z.string().max(10).nullable().optional(),
  defaultIssRateBp: z.number().int().nullable().optional(),
  issWithheldDefault: z.boolean().optional(),
  issDueCityIbgeCode: z.string().max(7).nullable().optional(),
  subjectToIrrf: z.boolean().optional(),
  subjectToCsll: z.boolean().optional(),
  subjectToInss: z.boolean().optional(),
  validFrom: z.iso.date().optional(),
  validUntil: z.iso.date().nullable().optional(),
});

export const UpsertTenantServiceTaxProfileResponseSchema = z.looseObject({
  profileId: z.uuid(),
  tenantServiceId: z.uuid(),
  validFrom: z.iso.date(),
  validUntil: z.iso.date().nullable(),
});

export const ListServiceTaxProfilesQuerySchema = z.looseObject({
  competenceDate: z.iso.date().optional(),
});

export const ServiceTaxProfileListItemSchema = z.looseObject({
  id: z.uuid(),
  taxCodeNational: z.string().nullable(),
  municipalServiceCode: z.string().nullable(),
  lc116Code: z.string().nullable(),
  cnaeCode: z.string().nullable(),
  defaultIssRateBp: z.number().nullable(),
  issWithheldDefault: z.boolean(),
  issDueCityIbgeCode: z.string().nullable(),
  subjectToIrrf: z.boolean(),
  subjectToCsll: z.boolean(),
  subjectToInss: z.boolean(),
  validFrom: z.iso.date(),
  validUntil: z.iso.date().nullable(),
});

export const ListServiceTaxProfilesResponseSchema = z.looseObject({
  items: z.array(ServiceTaxProfileListItemSchema),
  activeProfile: ServiceTaxProfileListItemSchema.nullable(),
});

export const ProductIdParamsSchema = z.looseObject({ productId: z.uuid() });
export const ServiceIdParamsSchema = z.looseObject({ serviceId: z.uuid() });

export const GetProductTaxProfileSuggestionQuerySchema = z.looseObject({
  placeId: z.uuid(),
});

export const GetProductTaxProfileSuggestionResponseSchema = z.looseObject({
  suggestionId: z.uuid(),
  tenantProductId: z.uuid(),
  catalogGenericProductId: z.uuid(),
  originCode: z.string().length(1),
  ncmCode: z.string(),
  cestCode: z.string().nullable(),
  defaultCfopInternalCode: z.string().nullable(),
  defaultCfopInterstateCode: z.string().nullable(),
  pisCstCode: z.string().nullable(),
  cofinsCstCode: z.string().nullable(),
  ipiCstCode: z.string().nullable(),
});

export type GetProductTaxProfileSuggestionQuery = z.infer<
  typeof GetProductTaxProfileSuggestionQuerySchema
>;
export type GetProductTaxProfileSuggestionResponse = z.infer<
  typeof GetProductTaxProfileSuggestionResponseSchema
>;

export type UpsertTenantProductTaxProfileBody = z.infer<
  typeof UpsertTenantProductTaxProfileBodySchema
>;
export type UpsertTenantServiceTaxProfileBody = z.infer<
  typeof UpsertTenantServiceTaxProfileBodySchema
>;
